import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Star, Sparkles } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import useLenis from "@/hooks/useLenis";
import UnevoHeader from "@/components/unevo/UnevoHeader";
import UnevoContact from "@/components/unevo/UnevoContact";
import { SERVICES } from "@/data-unevo";
import { SERVICE_DETAILS } from "@/service-details";

export default function ServiceDetail() {
  const { id } = useParams();
  useLenis();
  const service = SERVICES.find((s) => s.id === id);
  const detail = SERVICE_DETAILS[id];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!service || !detail) return <Navigate to="/" replace />;

  return (
    <div className="bg-[#0B0F1A] text-white font-body antialiased">
      <UnevoHeader />
      <main>
        {/* Hero */}
        <section
          data-testid={`service-hero-${service.id}`}
          className="relative overflow-hidden bg-[#0B0F1A] pt-40 pb-20 md:pb-28"
        >
          <div className="pointer-events-none absolute -right-32 top-24 h-96 w-96 rounded-full bg-vibrant-orange/15 blur-3xl" />
          <div className="pointer-events-none absolute -left-32 bottom-0 h-80 w-80 rounded-full bg-emerald-green/10 blur-3xl" />
          <div className="relative mx-auto max-w-5xl px-6 md:px-12">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/50 hover:text-white"
              data-testid="service-back-link"
            >
              <ArrowLeft size={14} /> All Services
            </Link>
            <div className="mt-6 flex items-center gap-3">
              <span className="rounded-full border border-vibrant-orange/30 bg-vibrant-orange/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-vibrant-orange">
                {service.tag} · {service.kicker}
              </span>
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 font-heading text-5xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
            >
              {service.title}.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg"
            >
              {detail.longDesc}
            </motion.p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#service-lead"
                data-testid="service-hero-cta"
                className="inline-flex items-center gap-2 rounded-full bg-vibrant-orange px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-vibrant-orange/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-orange-600"
              >
                Get a quote for {service.tag.toLowerCase()}
                <ArrowUpRight size={16} />
              </a>
              <Link
                to="/#services"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-deep-trust-blue"
              >
                See other services
              </Link>
            </div>
          </div>
        </section>

        {/* Deliverables */}
        <section className="border-t border-white/5 bg-[#0B0F1A] py-20 md:py-24">
          <div className="mx-auto max-w-5xl px-6 md:px-12">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-green">
              What&apos;s included
            </span>
            <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
              Everything you get in this engagement.
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
              {detail.deliverables.map((d, i) => (
                <motion.div
                  key={d}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ delay: i * 0.05, duration: 0.5 }}
                  className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5"
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-emerald-green/15 text-emerald-green">
                    <Check size={18} />
                  </span>
                  <p className="text-sm leading-relaxed text-white/80">{d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Tiers */}
        <section
          data-testid="service-tiers"
          className="bg-light-gray py-24 text-deep-trust-blue md:py-28"
        >
          <div className="mx-auto max-w-6xl px-6 md:px-12">
            <div className="mb-12 max-w-2xl">
              <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-green">
                Pricing tiers
              </span>
              <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
                Simple, transparent, scalable.
              </h2>
              <p className="mt-3 text-sm text-deep-trust-blue/60 md:text-base">
                Every tier includes weekly progress demos and no lock-in. Move up or down as your needs change.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {detail.tiers.map((t, i) => (
                <motion.div
                  key={t.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: i * 0.08, duration: 0.6 }}
                  data-testid={`service-tier-${t.name.toLowerCase().replace(/\s/g, "-")}`}
                  className={`relative flex flex-col rounded-3xl border p-8 transition-all duration-500 hover:-translate-y-2 ${
                    t.highlight
                      ? "border-vibrant-orange/40 bg-deep-trust-blue text-white shadow-2xl shadow-deep-trust-blue/25"
                      : "border-deep-trust-blue/10 bg-white hover:shadow-xl"
                  }`}
                >
                  {t.highlight && (
                    <span className="absolute -top-3 left-8 rounded-full bg-vibrant-orange px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white">
                      Most popular
                    </span>
                  )}
                  <span className={`text-xs font-semibold uppercase tracking-widest ${t.highlight ? "text-emerald-green" : "text-emerald-green"}`}>
                    {t.name}
                  </span>
                  <p className="mt-2 font-heading text-3xl font-extrabold">{t.price}</p>
                  <p className={`mt-2 text-sm ${t.highlight ? "text-white/70" : "text-deep-trust-blue/60"}`}>
                    {t.blurb}
                  </p>
                  <ul className={`mt-6 flex-1 space-y-3 text-sm ${t.highlight ? "text-white/80" : "text-deep-trust-blue/70"}`}>
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check size={16} className="mt-0.5 shrink-0 text-emerald-green" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#service-lead"
                    className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-all duration-300 ${
                      t.highlight
                        ? "bg-vibrant-orange text-white hover:bg-orange-600"
                        : "bg-deep-trust-blue text-white hover:bg-deep-trust-blue/90"
                    }`}
                  >
                    Choose {t.name}
                    <ArrowUpRight size={16} />
                  </a>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Case studies */}
        <section
          data-testid="service-case-studies"
          className="bg-[#0B0F1A] py-24 text-white md:py-28"
        >
          <div className="mx-auto max-w-6xl px-6 md:px-12">
            <div className="mb-12 max-w-2xl">
              <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-emerald-green">
                <Sparkles size={16} /> Case studies
              </span>
              <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
                Real outcomes, real numbers.
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {detail.caseStudies.map((c, i) => (
                <motion.article
                  key={c.client}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                  className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-8 transition-all duration-500 hover:-translate-y-1 hover:border-emerald-green/30"
                >
                  <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-vibrant-orange/10 blur-3xl transition-all group-hover:bg-vibrant-orange/25" />
                  <div className="relative">
                    <span className="text-xs font-semibold uppercase tracking-widest text-emerald-green">
                      {c.industry}
                    </span>
                    <h3 className="mt-3 font-heading text-2xl font-bold tracking-tight">
                      {c.client}
                    </h3>
                    <p className="mt-4 text-base leading-relaxed text-white/70">{c.result}</p>
                    <div className="mt-6 flex items-center gap-1 text-vibrant-orange">
                      {Array.from({ length: 5 }).map((_, k) => (
                        <Star key={k} size={14} fill="currentColor" />
                      ))}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Dedicated Lead Form (reuses UnevoContact which posts to /api/leads) */}
        <section id="service-lead">
          <UnevoContact />
        </section>
      </main>

      <Toaster position="top-center" richColors />
    </div>
  );
}
